import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_08b1829c from 'nuxt_plugin_sentryserver_08b1829c' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_ba0fddd8 from 'nuxt_plugin_sentryclient_ba0fddd8' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_templatesplugin13d49d6b_46dfb71f from 'nuxt_plugin_templatesplugin13d49d6b_46dfb71f' // Source: ./templates.plugin.13d49d6b.js (mode: 'all')
import nuxt_plugin_nuxtenv_7ec1c37d from 'nuxt_plugin_nuxtenv_7ec1c37d' // Source: ./nuxt-env.js (mode: 'all')
import nuxt_plugin_vuescrollto_0d338831 from 'nuxt_plugin_vuescrollto_0d338831' // Source: ./vue-scrollto.js (mode: 'client')
import nuxt_plugin_axios_053c1ad8 from 'nuxt_plugin_axios_053c1ad8' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_nuxtheadway_57ff0e9b from 'nuxt_plugin_nuxtheadway_57ff0e9b' // Source: ./nuxt-headway.js (mode: 'client')
import nuxt_plugin_gtm_7ab5b7ae from 'nuxt_plugin_gtm_7ab5b7ae' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_router_5693cbd7 from 'nuxt_plugin_router_5693cbd7' // Source: ./router.js (mode: 'all')
import nuxt_plugin_features_3c77ac9a from 'nuxt_plugin_features_3c77ac9a' // Source: ../plugins/features (mode: 'all')
import nuxt_plugin_vuetify_d1c64f56 from 'nuxt_plugin_vuetify_d1c64f56' // Source: ../plugins/libraries/vuetify.js (mode: 'all')
import nuxt_plugin_filters_2dd71012 from 'nuxt_plugin_filters_2dd71012' // Source: ../plugins/filters.js (mode: 'all')
import nuxt_plugin_directives_521c0486 from 'nuxt_plugin_directives_521c0486' // Source: ../plugins/directives.js (mode: 'all')
import nuxt_plugin_axios_3566aa80 from 'nuxt_plugin_axios_3566aa80' // Source: ../plugins/axios (mode: 'all')
import nuxt_plugin_export_69c182ac from 'nuxt_plugin_export_69c182ac' // Source: ../plugins/export (mode: 'client')
import nuxt_plugin_sentry_e47869fc from 'nuxt_plugin_sentry_e47869fc' // Source: ../plugins/sentry.js (mode: 'all')
import nuxt_plugin_vuewaypointclient_2af38ff2 from 'nuxt_plugin_vuewaypointclient_2af38ff2' // Source: ../plugins/libraries/vue-waypoint.client.js (mode: 'client')
import nuxt_plugin_vcalendarclient_268d4e02 from 'nuxt_plugin_vcalendarclient_268d4e02' // Source: ../plugins/libraries/v-calendar.client.js (mode: 'client')
import nuxt_plugin_vtooltipclient_7d16e05e from 'nuxt_plugin_vtooltipclient_7d16e05e' // Source: ../plugins/libraries/v-tooltip.client.js (mode: 'client')
import nuxt_plugin_intercomclient_b36f7722 from 'nuxt_plugin_intercomclient_b36f7722' // Source: ../plugins/libraries/intercom.client.js (mode: 'client')
import nuxt_plugin_plottableclient_4f591e02 from 'nuxt_plugin_plottableclient_4f591e02' // Source: ../plugins/libraries/plottable.client.js (mode: 'client')
import nuxt_plugin_authclient_79f5427c from 'nuxt_plugin_authclient_79f5427c' // Source: ../plugins/auth.client.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Nozzle","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"Nozzle"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.png"},{"rel":"preconnect","href":"https:\u002F\u002Ffonts.googleapis.com"},{"rel":"preconnect","href":"https:\u002F\u002Ffonts.gstatic.com","crossorigin":true},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss2?family=Be+Vietnam:wght@300;400;600;700&display=swap"}],"style":[],"script":[{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){if(w.doNotTrack||w[x][i])return;w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.defer=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);};w[y]('GTM-WD5VN8K')})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"},{"innerHTML":"window.HW_config = {\"selector\":\".dumb-selector\",\"trigger\":\".dumb-selector\",\"account\":\"7Kb3Bx\"};"},{"src":"https:\u002F\u002Fcdn.headwayapp.co\u002Fwidget.js","async":true}],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":"\u003Ciframe src=\"https:\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-WD5VN8K&\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\" title=\"gtm\"\u003E\u003C\u002Fiframe\u003E"}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]},"__dangerouslyDisableSanitizers":["script"]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_08b1829c === 'function') {
    await nuxt_plugin_sentryserver_08b1829c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_ba0fddd8 === 'function') {
    await nuxt_plugin_sentryclient_ba0fddd8(app.context, inject)
  }

  if (typeof nuxt_plugin_templatesplugin13d49d6b_46dfb71f === 'function') {
    await nuxt_plugin_templatesplugin13d49d6b_46dfb71f(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtenv_7ec1c37d === 'function') {
    await nuxt_plugin_nuxtenv_7ec1c37d(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuescrollto_0d338831 === 'function') {
    await nuxt_plugin_vuescrollto_0d338831(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_053c1ad8 === 'function') {
    await nuxt_plugin_axios_053c1ad8(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_nuxtheadway_57ff0e9b === 'function') {
    await nuxt_plugin_nuxtheadway_57ff0e9b(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_7ab5b7ae === 'function') {
    await nuxt_plugin_gtm_7ab5b7ae(app.context, inject)
  }

  if (typeof nuxt_plugin_router_5693cbd7 === 'function') {
    await nuxt_plugin_router_5693cbd7(app.context, inject)
  }

  if (typeof nuxt_plugin_features_3c77ac9a === 'function') {
    await nuxt_plugin_features_3c77ac9a(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetify_d1c64f56 === 'function') {
    await nuxt_plugin_vuetify_d1c64f56(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_2dd71012 === 'function') {
    await nuxt_plugin_filters_2dd71012(app.context, inject)
  }

  if (typeof nuxt_plugin_directives_521c0486 === 'function') {
    await nuxt_plugin_directives_521c0486(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_3566aa80 === 'function') {
    await nuxt_plugin_axios_3566aa80(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_export_69c182ac === 'function') {
    await nuxt_plugin_export_69c182ac(app.context, inject)
  }

  if (typeof nuxt_plugin_sentry_e47869fc === 'function') {
    await nuxt_plugin_sentry_e47869fc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuewaypointclient_2af38ff2 === 'function') {
    await nuxt_plugin_vuewaypointclient_2af38ff2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vcalendarclient_268d4e02 === 'function') {
    await nuxt_plugin_vcalendarclient_268d4e02(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vtooltipclient_7d16e05e === 'function') {
    await nuxt_plugin_vtooltipclient_7d16e05e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_intercomclient_b36f7722 === 'function') {
    await nuxt_plugin_intercomclient_b36f7722(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_plottableclient_4f591e02 === 'function') {
    await nuxt_plugin_plottableclient_4f591e02(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_authclient_79f5427c === 'function') {
    await nuxt_plugin_authclient_79f5427c(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
